import { FC, useMemo, useState } from "react";
import * as _ from "lodash";

import {
  Button,
  PlusOutlined,
  Row,
  Typography,
  Analytics,
  Modal,
  TabsProps,
  DeleteOutlined,
  Space,
  AntDesignInput,
  Spin,
  DraggableTabs,
  EditOutlined,
  message,
  CopyOutlined,
  ChartComparison,
  Icon
} from "@ctra/components";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { Enterprise } from "@ctra/api";
import { useGoogleAnalytics } from "@ctra/analytics";

import { CompiledRoutes, Routes } from "@routes";
import { useFarm } from "@farms";
import { useDataDictionary } from "@base";
import { GAActions, useSavedCharts } from "@analytics";
import { ChartList } from "../ChartList";
import { CopyDashboardModal } from "../CopyDashboardModal";

import { useTabs } from "./hooks";

import styles from "./DashboardPage.module.less";

const { confirm } = Modal;
const { Title, Text } = Typography;
const {
  V3: { Empty }
} = Analytics;

export const commonModalProps = {
  className: styles.Modal,
  closable: true,
  icon: false,
  okButtonProps: { className: styles.OkBtn }
};

/**
 * Dashboard Page with tabs
 * @returns
 */
export const DashboardPage: FC = () => {
  const { t } = useTranslation();
  const { trackEvent } = useGoogleAnalytics();
  const { farm } = useFarm();
  const { dataDescriptors } = useDataDictionary();
  const [copyModalVisible, setCopyModalVisible] = useState(false);

  const {
    savedCharts,
    api: { updateTabOrder, renameTab },
    meta: { isLoading, isUpdating, tabsUpdating }
  } = useSavedCharts();

  const {
    analytics: {
      v3: {
        analysis,
        addDashboard,
        addCta,
        pageDescription,
        confirm: {
          newTab,
          removeTab,
          renameTab: renameTabCopy,
          copyTab,
          error: { duplicateTab }
        },
        empty
      }
    },
    gamePlan: {
      actions: {
        delete: { confirm: confirmCopy }
      }
    },
    impactTracking: {
      add: { cta }
    },
    timeline: {
      createEvent: { submit }
    }
  } = Content;

  /**
   * Route to config page
   */
  const handleAdd = (groupName?: string) => {
    trackEvent(GAActions.goToChartConfig);

    Enterprise.history.push({
      pathname: Routes.app.analytics.configure.index,
      state: { dashboard: groupName || activeKey }
    });
  };

  /**
   * Handle tab renaming with confirmation
   * @param groupName
   */
  const handleRename = (groupName: string) => {
    let newTabLabel = groupName;
    confirm({
      ...commonModalProps,
      okText: t(submit),
      title: (
        <Space>
          <EditOutlined />
          {t(renameTabCopy.title)}
        </Space>
      ),
      content: (
        <Space direction="vertical">
          <Text>{t(renameTabCopy.description)}</Text>
          <AntDesignInput
            placeholder={t(newTab.inputPlaceholder)}
            defaultValue={groupName}
            onChange={({ target: { value } }) => {
              newTabLabel = value;
            }}
          />
        </Space>
      ),
      onOk() {
        if (_.includes(_.map(savedCharts, "groupName"), newTabLabel)) {
          message.error(t(duplicateTab));
        } else {
          trackEvent(GAActions.renameDashboard);
          renameTab(groupName, newTabLabel);
          setActiveKey(newTabLabel);
        }
      }
    });
  };

  /**
   * Initial items memoized
   */
  const initialItems = useMemo(
    () =>
      _.map(savedCharts, ({ charts, groupName }) => ({
        label: groupName,
        key: groupName,
        children: (
          <>
            <Row justify="end">
              <Button
                type="link"
                icon={<EditOutlined />}
                className={styles.Link}
                onClick={() => handleRename(groupName)}
              >
                {t(renameTabCopy.title)}
              </Button>
              <Button
                type="link"
                icon={<CopyOutlined />}
                className={styles.Link}
                onClick={() => setCopyModalVisible(true)}
              >
                {t(copyTab.title)}
              </Button>
            </Row>
            {_.isEmpty(charts) ? (
              <Empty
                className={styles.Empty}
                variant="charts"
                title={t(empty.charts.title)}
                description={
                  <Space size="middle" direction="vertical">
                    <Button type="link" onClick={() => handleAdd(groupName)}>
                      {t(empty.charts.cta)}
                    </Button>
                    {t(empty.charts.description)}
                  </Space>
                }
              />
            ) : (
              <ChartList charts={charts} groupName={groupName} />
            )}
          </>
        )
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedCharts, farm, dataDescriptors]
  );

  const { onChange, onEdit, activeKey, setActiveKey, items } = useTabs(initialItems);

  /**
   * Add or remove a dashboard
   * @param key
   * @param action
   */
  const handleEdit: TabsProps["onEdit"] = (key, action) => {
    if (action === "remove") {
      /**
       * Opens up a confirmation modal
       */
      confirm({
        ...commonModalProps,
        okText: t(confirmCopy.yes),
        cancelText: t(confirmCopy.no),
        title: (
          <Space>
            <DeleteOutlined />
            {t(removeTab.title)}
          </Space>
        ),
        content: t(removeTab.description),
        onOk() {
          trackEvent(GAActions.removeDashboard);
          onEdit(key, action);
        }
      });
    }

    if (action === "add") {
      let newTabLabel: string;
      confirm({
        ...commonModalProps,
        okText: t(cta),
        title: (
          <Space>
            <PlusOutlined />
            {t(newTab.title)}
          </Space>
        ),
        content: (
          <Space direction="vertical">
            <Text>{t(newTab.description)}</Text>
            <AntDesignInput
              placeholder={t(newTab.inputPlaceholder)}
              onChange={({ target: { value } }) => {
                newTabLabel = value;
              }}
            />
          </Space>
        ),
        onOk() {
          if (_.includes(_.map(savedCharts, "groupName"), newTabLabel)) {
            message.error(t(duplicateTab));
          } else {
            trackEvent(GAActions.addDashboard);
            onEdit(key, action, newTabLabel);
          }
        }
      });
    }
  };

  const addBtn = (
    <Space size="large">
      <Button type="primary" size="large" icon={<PlusOutlined />} onClick={() => handleAdd()}>
        {t(addCta)}
      </Button>
      <Button
        type="secondary"
        size="large"
        icon={<Icon component={ChartComparison} />}
        onClick={() => {
          trackEvent(GAActions.goToAnalysisDirectly);

          Enterprise.history.push({
            /**
             * not a good practise, but gets the job done
             */
            pathname: CompiledRoutes.app.analytics.configure.metric({ metricID: "analysis" })
          });
        }}
      >
        {t(analysis)}
      </Button>
    </Space>
  );

  return (
    <>
      <Title level={6} className={styles.Header}>
        {t(pageDescription)}
      </Title>
      <Spin spinning={isLoading || isUpdating || tabsUpdating}>
        {_.isEmpty(items) ? (
          <>
            <Row justify="space-between">
              <Button
                icon={<PlusOutlined />}
                type="secondary"
                size="large"
                onClick={() => handleEdit("empty", "add")}
              >
                {t(addDashboard)}
              </Button>
              {addBtn}
            </Row>
            <Empty
              className={styles.Empty}
              variant="dashboards"
              title={t(empty.dashboards.title)}
              description={
                <Space size="middle" direction="vertical">
                  <Button type="link" onClick={() => handleEdit("empty", "add")}>
                    {t(empty.dashboards.cta)}
                  </Button>
                  {t(empty.dashboards.description)}
                </Space>
              }
            />
          </>
        ) : (
          <DraggableTabs
            handleDrag={updateTabOrder}
            type="editable-card"
            onChange={onChange}
            activeKey={activeKey}
            onEdit={handleEdit}
            items={items}
            tabBarExtraContent={addBtn}
          />
        )}
      </Spin>
      <CopyDashboardModal
        visible={copyModalVisible}
        onCancel={() => setCopyModalVisible(false)}
        group={activeKey}
      />
    </>
  );
};
